import { Colors } from '../../../theme';
import { LegacyColorMap } from '@/theme/LegacyColorMap';
import type { AnchorTarget } from '../../../ts-types';
import { Icon, IconName } from '../../display/Icon';
import { Tag } from '../../display/Tag';
import { Grid } from '../../layout/Grid';
import { LegacyButton } from '../../navigation/LegacyButton';
import { Link } from '../../navigation/Link';
import styles from './TwoColumnRiver.module.scss';
import { clsx } from 'clsx';

import { Heading, HeadingLevel } from '../../display/Heading';
export type Props = {
  /** Background color of the entire element */
  backgroundColor?: 'blue10' | 'cream' | 'neutral100' | 'neutral200';

  /** Color style of the primary CTA button */
  ctaColor?: 'primary' | 'primary-alt' | 'secondary' | 'brand';

  /** href for the primary CTA button */
  ctaHref?: string;

  /** target for the primary CTA button */
  ctaTarget?: AnchorTarget;

  /** Text content to render for the primary CTA button */
  ctaText?: React.ReactNode;

  /** Text content to render for the description */
  description?: React.ReactNode;

  /** Text content to render for the heading */
  heading: string;

  /** Reverses the order of the text content and media */
  isReversed?: boolean;

  /** Media to render */
  media?: React.ReactNode;

  /** Click handler for the primary CTA button */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;

  /** Key down handler for the primary CTA button */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;

  /** Click handler for the secondary CTA button */
  onSecondaryCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;

  /** Key down handler for the secondary CTA button */
  onSecondaryCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;

  /** Text content to render for the secondary CTA button */
  secondaryCtaText?: React.ReactNode;

  /** href for the secondary CTA button */
  secondaryCtaHref?: string;
  /**
   * A label that renders as a small tag
   * with a background color and an icon
   */
  label?: string;
  /**
   * The icon that is displayed in the label.
   */
  labelIcon?: IconName;
  /**
   * The labelColor determines the background-color of the label.
   */
  labelColor?:
    | 'default'
    | 'success'
    | 'error'
    | 'warning'
    | 'inverted'
    | 'simple';
  /**
   * An optional classname used for style overrides.
   */
  className?: string;
};

export const TwoColumnRiver = ({
  backgroundColor = 'neutral100',
  ctaColor = 'primary',
  ctaHref,
  ctaText,
  ctaTarget,
  description,
  heading,
  isReversed = false,
  media,
  onCtaClick,
  onCtaKeyDown,
  onSecondaryCtaClick,
  onSecondaryCtaKeyDown,
  secondaryCtaHref,
  secondaryCtaText,
  label,
  labelIcon,
  labelColor,
  className,
}: Props) => {
  const getCtaComponent = () => {
    const sharedProps = {
      children: ctaText,
      href: ctaHref,
      onClick: onCtaClick,
      onKeyDown: onCtaKeyDown,
      target: ctaTarget,
    };

    return (
      <LegacyButton
        {...sharedProps}
        // "primary-alt" no longer exists as of V8 and is now mapped to "primary"
        color={ctaColor === 'primary-alt' ? 'primary' : ctaColor}
      />
    );
  };
  return (
    <div
      className={clsx(styles.root, className)}
      style={{
        '--_zds-twocolumnriver-background-color':
          Colors[LegacyColorMap[backgroundColor]],
      }}
      data-zds
    >
      <div
        className={clsx(styles.container, 'twoColumnRiver-container')}
        data-reversed={isReversed ? true : undefined}
        data-zds
      >
        <div
          className={clsx(styles.content, 'twoColumnRiver-content')}
          data-zds
        >
          {label && (
            <div
              className={clsx(
                styles.labelContainer,
                'twoColumnRiver-labelContainer'
              )}
              data-zds
            >
              <Tag variant={labelColor}>
                {labelIcon ? (
                  <Grid alignItems="center" columns="auto max-content" gap={5}>
                    <Icon name={labelIcon} size={20} />
                    {label}
                  </Grid>
                ) : (
                  label
                )}
              </Tag>
            </div>
          )}
          <HeadingLevel>
            <Heading className={styles.heading}>{heading}</Heading>
          </HeadingLevel>
          {description && (
            <div
              className={clsx(styles.description, 'twoColumnRiver-description')}
              data-zds
            >
              {description}
            </div>
          )}
          {ctaText && (ctaHref || onCtaClick) && (
            <div className={clsx(styles.cta, 'twoColumnRiver-cta')} data-zds>
              {getCtaComponent()}
            </div>
          )}
          {secondaryCtaText && secondaryCtaHref && (
            <div
              className={clsx(
                styles.secondaryCta,
                'twoColumnRiver-secondaryCta'
              )}
              data-zds
            >
              <Link
                href={secondaryCtaHref}
                onClick={onSecondaryCtaClick}
                onKeyDown={onSecondaryCtaKeyDown}
                weight="bold"
              >
                {secondaryCtaText}
              </Link>
            </div>
          )}
        </div>
        {media && (
          <div
            className={clsx(styles.media, 'twoColumnRiver-media')}
            data-reversed={isReversed ? true : undefined}
            data-zds
          >
            {media}
          </div>
        )}
      </div>
    </div>
  );
};
